import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';
import theme from 'assets/theme';
import themeRTL from 'assets/theme/theme-rtl';
import themeDark from 'assets/theme-dark';
import themeDarkRTL from 'assets/theme-dark/theme-rtl';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import routes from 'routes';
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from 'context';
import brandWhite from 'assets/images/deltaSnp/logo_Delta-Academy-web.png';
import brandDark from 'assets/images/deltaSnp/logo_Delta-Academy-web.png';
import { getUserDetails } from 'redux/userDetailsSlice';
import {
  setIsLogoutTiming,
  resetIsLogout,
} from 'examples/Navbars/store/logoutSlice';
import { TimeOutPopup } from 'utils/commonUtils';
import { resetTutoringAvailabilityDetails } from 'layouts/tutoring/store/tutoringAvailabilitySlice/tutoringAvailabilitySlice';
import { getTutoringSubjectDetails } from 'layouts/tutoring/store/tutoringSubjectsSlice/tutoringSubjectsSlice';
import { getTutoringSubjects } from 'layouts/tutoring/services/tutoringApi';
import { getDashboardDetails } from 'layouts/dashboards/store/dashboardSlice';
import { getDashboard } from 'layouts/dashboards/dashboard/services/dashboardApi';

const DeltaSnp = (props) => {
  const { signOut, user } = props;
  const dispatchUse = useDispatch();
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(60);
  //return (
  //  <div className="App">
  //    <h2>Welcome {user.attributes.email}</h2>
  //    <Button onClick={signOut}>Sign Out</Button>
  //  </div>
  //);
  const isLogoutTiming = useSelector((state) => state.logout.isLogoutTiming);

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const inactivityTimerRef = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      dispatchUse(setIsLogoutTiming(true));
    }, 9 * 60 * 1000);
  }, [dispatchUse]);
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    resetInactivityTimer();
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      clearTimeout(inactivityTimerRef.current);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [handleUserActivity, resetInactivityTimer]);

  useEffect(() => {
    if (isLogoutTiming) {
      const interval = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
      if (seconds === 0) {
        dispatchUse(setIsLogoutTiming(false));
        navigate('/');
        signOut();
        clearInterval(interval);
        setTimeout(() => {
          dispatchUse(resetIsLogout());
        }, 2000);
      }
      return () => {
        clearInterval(interval);
      };
    }
  }, [isLogoutTiming, seconds, dispatchUse, navigate, signOut]);

  const fetchTutoringSubjects = useCallback(async () => {
    const tutoringSubjects = await getTutoringSubjects();
    dispatchUse(getTutoringSubjectDetails(tutoringSubjects));
  }, [dispatchUse]);

  const fetchDashboard = useCallback(async () => {
    const dashboardData = await getDashboard();
    dispatchUse(getDashboardDetails(dashboardData));
  }, [dispatchUse]);

  // Runs once on mount to seed the store from the initial auth payload.
  // Must not re-run on token refresh, or it would wipe in-progress tutoring
  // availability selections.
  useEffect(() => {
    fetchAuthSession().then((session) => {
      dispatchUse(getUserDetails(session.tokens?.accessToken?.payload));
    });
    dispatchUse(resetTutoringAvailabilityDetails());
    fetchTutoringSubjects();
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: 'pointer' }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === 'rtl' ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === 'dashboard' && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName="Delta Student and Parent"
              routes={routes}
              user={user}
              signOut={signOut}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            <TimeOutPopup
              seconds={seconds}
              setSeconds={setSeconds}
              signOut={signOut}
            />
            {configsButton}
          </>
        )}
        {layout === 'vr' && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* {layout === 'dashboard' && (
        <> */}
      {/* <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="Delta Student and Parent"
            routes={routes}
            user={user}
            signOut={signOut}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          /> */}
      <TimeOutPopup
        seconds={seconds}
        setSeconds={setSeconds}
        signOut={signOut}
      />
      {/*  </> */}
      {/* )} */}
      {/* {layout === 'vr' && <Configurator />} */}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </ThemeProvider>
  );
};

export default DeltaSnp;
